import { Head } from '@inertiajs/react'
import { useState } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Briefcase, Mail, AlertTriangle, RefreshCw, CheckCircle, XCircle } from 'lucide-react'

interface IndexProps {
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
  account?: {
    id: number
    name: string
    gmailSessionId: number
  }
  isDevelopment?: boolean
}

interface FetchResult {
  gmailId: string
  subject: string
  status: 'processed' | 'skipped' | 'requeued'
  reason?: string
  emailId?: number
}

export default function Index({ currentUser, gmailConnection, account, isDevelopment }: IndexProps) {
  const [isFetching, setIsFetching] = useState(false)
  const [fetchMessage, setFetchMessage] = useState<string | null>(null)
  const [fetchResults, setFetchResults] = useState<FetchResult[] | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const handleFetchRecentEmails = async () => {
    setIsFetching(true)
    setFetchMessage(null)
    setFetchResults(null)
    setFetchError(null)

    try {
      const response = await fetch('/admin/fetch_recent_emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        setFetchError(data.error || 'Failed to fetch emails')
      } else {
        setFetchMessage(data.message)
        setFetchResults(data.results || null)
      }
    } catch (error) {
      setFetchError(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setIsFetching(false)
    }
  }

  return (
    <>
      <Head title="Admin Dashboard" />
      <AdminLayout currentUser={currentUser}>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Administrative tools and system monitoring
            </p>
          </div>

          {/* Development Mode Banner */}
          {isDevelopment && (
            <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-5 h-5" />
                  Development Environment
                </CardTitle>
                <CardDescription className="text-amber-600 dark:text-amber-400/80">
                  Automatic email processing is disabled. Gmail webhooks and polling jobs are skipped to reduce log noise.
                  Use the button below to manually fetch and process recent emails for testing.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleFetchRecentEmails}
                  disabled={isFetching || !gmailConnection?.active}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {isFetching ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Fetch up to 5 Recent Emails
                    </>
                  )}
                </Button>

                {!gmailConnection?.active && (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Gmail connection is not active. Enable email processing in settings first.
                  </p>
                )}

                {fetchError && (
                  <div className="p-3 rounded-md bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      {fetchError}
                    </div>
                  </div>
                )}

                {fetchMessage && (
                  <div className="p-3 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {fetchMessage}
                    </div>
                  </div>
                )}

                {fetchResults && fetchResults.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Results:</p>
                    <div className="space-y-1.5">
                      {fetchResults.map((result, index) => (
                        <div
                          key={index}
                          className="text-sm p-2 rounded bg-white dark:bg-gray-900/50 border border-amber-200 dark:border-amber-800"
                        >
                          <div className="flex items-center gap-2">
                            {result.status === 'processed' && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 rounded">
                                Processed
                              </span>
                            )}
                            {result.status === 'requeued' && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 rounded">
                                Requeued
                              </span>
                            )}
                            {result.status === 'skipped' && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 rounded">
                                Skipped ({result.reason})
                              </span>
                            )}
                            <span className="truncate text-gray-700 dark:text-gray-300">
                              {result.subject || '(no subject)'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                  <Briefcase className="w-5 h-5" />
                  Background Jobs
                </CardTitle>
                <CardDescription>
                  Monitor and manage background job queues, recurring tasks, and job history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild>
                  <a href="/admin/jobs">
                    View Mission Control
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                  <Mail className="w-5 h-5" />
                  Email Inspector
                </CardTitle>
                <CardDescription>
                  Search and inspect all emails fetched from Gmail. Debug processing issues and see why emails were skipped.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild>
                  <a href="/admin/emails">
                    Open Email Inspector
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </AdminLayout>
    </>
  )
}
